import { createContext, PropsWithChildren, useContext, useState } from 'react';
import { TabType } from './ViewInvestor/types';
import { createViewVehicleApplicationRoute } from 'adminConstants/routes';

type ContextType = {
  selectedVehicleApplicationId: string | null;
  selectedVehicleApplicationTab: TabType | null;
  showCompleteCommitmentFrom: boolean;
  goToVehicleApplication: (
    vehicleApplicationId: string,
    tab?: TabType,
    showCompleteCommitmentFrom?: boolean,
  ) => void;
  closeVehicleApplication: () => void;
};

type SelectedVehicleApplication = {
  id: string;
  tab?: TabType;
  showCompleteCommitmentFrom?: boolean;
};

const OnboardingOverviewNavigationContext = createContext<ContextType>({
  selectedVehicleApplicationId: null,
  selectedVehicleApplicationTab: null,
  showCompleteCommitmentFrom: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  goToVehicleApplication: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  closeVehicleApplication: () => {},
});

const getVehicleApplicationFromUrl = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const vehicleApplicationId = searchParams.get('vehicleApplicationId');
  if (!vehicleApplicationId) {
    return null;
  }
  const tab = searchParams.get('tab') as TabType | null;
  const showCompleteCommitmentFrom = searchParams.get(
    'showCompleteCommitmentFrom',
  );

  return {
    id: vehicleApplicationId,
    tab: tab ?? TabType.InvestorDetails,
    showCompleteCommitmentFrom: !!showCompleteCommitmentFrom,
  };
};

export const OnboardingOverviewNavigationContextProvider: React.FC<
  PropsWithChildren
> = ({ children }) => {
  const [selectedVehicleApplication, setSelectedVehicleApplication] =
    useState<SelectedVehicleApplication | null>(() =>
      getVehicleApplicationFromUrl(),
    );

  const goToVehicleApplication = (
    vehicleApplicationId: string,
    tab?: TabType,
    showCompleteCommitmentFrom?: boolean,
  ) => {
    setSelectedVehicleApplication({
      id: vehicleApplicationId,
      tab: tab ?? TabType.InvestorDetails,
      showCompleteCommitmentFrom: showCompleteCommitmentFrom ?? false,
    });

    const url = showCompleteCommitmentFrom
      ? createViewVehicleApplicationRoute(vehicleApplicationId, tab) +
        '&showCompleteCommitmentFrom=true'
      : createViewVehicleApplicationRoute(vehicleApplicationId, tab);

    window.history.replaceState(null, '', url);
  };

  const closeVehicleApplication = () => {
    setSelectedVehicleApplication(null);
    window.history.replaceState(null, '', window.location.pathname);
  };

  return (
    <OnboardingOverviewNavigationContext.Provider
      value={{
        selectedVehicleApplicationId: selectedVehicleApplication?.id ?? null,
        selectedVehicleApplicationTab: selectedVehicleApplication?.tab ?? null,
        showCompleteCommitmentFrom:
          selectedVehicleApplication?.showCompleteCommitmentFrom ?? false,
        goToVehicleApplication,
        closeVehicleApplication,
      }}
    >
      {children}
    </OnboardingOverviewNavigationContext.Provider>
  );
};

//eslint-disable-next-line react-refresh/only-export-components
export const useOnboardingOverviewNavigationContext = () =>
  useContext(OnboardingOverviewNavigationContext);

export default OnboardingOverviewNavigationContextProvider;
